/**
 * Display-only coarsening of a catalogue item's score history into columns:
 * daily for the last 7 days, weekly for the next 3 weeks, quarterly for the
 * next ~year (capped to the 3 most recent quarters with data), yearly beyond
 * that (uncapped). Storage already retains full daily granularity for 60
 * days (score-history.ts's retention cascade) — this groups that data for
 * the table's column set; it deletes nothing.
 */

export interface HistoryEntry {
  t: string; // ISO day, e.g. "2026-07-15"
  s: number;
}

export type HistoryTier = 'daily' | 'weekly' | 'quarterly' | 'yearly';

export interface BucketedPoint {
  key: string;
  tier: HistoryTier;
  s: number;
  /** The entry that survived dedup in this bucket (latest wins). */
  t: string;
}

const DAILY_DAYS = 7;
const WEEKLY_WEEKS = 3;
const QUARTERLY_CUTOFF_DAYS = 365;
const QUARTERLY_MAX_COLUMNS = 3;

const TIER_ORDER: Record<HistoryTier, number> = { yearly: 0, quarterly: 1, weekly: 2, daily: 3 };

function utcDayMs(iso: string): number {
  return new Date(`${iso}T00:00:00.000Z`).getTime();
}

function ageDays(iso: string, now: Date): number {
  const nowMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.floor((nowMs - utcDayMs(iso)) / 86_400_000);
}

function quarterKey(iso: string): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  const q = Math.floor(d.getUTCMonth() / 3) + 1;
  return `Q#${d.getUTCFullYear()}-Q${q}`;
}

function yearKey(iso: string): string {
  return `Y#${new Date(`${iso}T00:00:00.000Z`).getUTCFullYear()}`;
}

/**
 * Bucket one item's history entries into display columns, keeping the LATEST
 * entry per bucket. Pure + deterministic (`now` injected for tests).
 */
export function bucketScoreHistory(history: HistoryEntry[], now: Date): BucketedPoint[] {
  const buckets = new Map<string, BucketedPoint>();
  for (const e of history) {
    const age = ageDays(e.t, now);
    let key: string;
    let tier: HistoryTier;
    if (age < DAILY_DAYS) {
      key = `D#${e.t}`;
      tier = 'daily';
    } else if (age < DAILY_DAYS + WEEKLY_WEEKS * 7) {
      const weekIdx = Math.floor((age - DAILY_DAYS) / 7);
      key = `W#${weekIdx}`;
      tier = 'weekly';
    } else if (age < QUARTERLY_CUTOFF_DAYS) {
      key = quarterKey(e.t);
      tier = 'quarterly';
    } else {
      key = yearKey(e.t);
      tier = 'yearly';
    }
    const existing = buckets.get(key);
    if (!existing || e.t >= existing.t) buckets.set(key, { key, tier, s: e.s, t: e.t });
  }

  // Cap quarterly to the 3 most recent quarters with data ("each year" stays uncapped).
  const quarterly = [...buckets.values()]
    .filter((b) => b.tier === 'quarterly')
    .sort((a, b) => (a.key < b.key ? 1 : -1)); // Q#year-Qn sorts chronologically as a string
  for (const b of quarterly.slice(QUARTERLY_MAX_COLUMNS)) buckets.delete(b.key);

  return sortBuckets([...buckets.values()]);
}

/** Oldest → newest, grouped by tier (yearly, quarterly, weekly, daily). */
export function sortBuckets<T extends Pick<BucketedPoint, 'tier' | 'key'>>(points: T[]): T[] {
  return [...points].sort((a, b) => {
    if (a.tier !== b.tier) return TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
    // Weekly keys are `W#<idx>`, higher idx = further in the past, so that
    // comparison runs in reverse of the others (which sort lexicographically
    // oldest-first).
    if (a.tier === 'weekly') return Number(b.key.slice(2)) - Number(a.key.slice(2));
    return a.key < b.key ? -1 : 1;
  });
}

/** Union of bucket keys across multiple items' bucketed points, sorted oldest → newest. */
export function mergeBucketColumns(perItem: BucketedPoint[][]): Pick<BucketedPoint, 'tier' | 'key'>[] {
  const byKey = new Map<string, Pick<BucketedPoint, 'tier' | 'key'>>();
  for (const points of perItem) {
    for (const p of points) if (!byKey.has(p.key)) byKey.set(p.key, { tier: p.tier, key: p.key });
  }
  return sortBuckets([...byKey.values()]);
}

/**
 * Display label for a bucket. Daily returns the raw ISO day (caller formats
 * via the app's date formatter); weekly resolves its relative index to a
 * calendar date (the window's last day, closest to `now`); quarterly/yearly
 * are derived straight from the key, independent of any item's data — so the
 * same bucket always labels identically across every item/column.
 */
export function bucketLabel(b: Pick<BucketedPoint, 'tier' | 'key'>, now: Date): string {
  if (b.tier === 'daily') return b.key.slice(2);
  if (b.tier === 'yearly') return b.key.slice(2);
  if (b.tier === 'quarterly') {
    const [year, q] = b.key.slice(2).split('-Q');
    return `T${q} ${year}`;
  }
  const weekIdx = Number(b.key.slice(2));
  const endAge = DAILY_DAYS + weekIdx * 7;
  const anchor = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - endAge));
  return anchor.toISOString().slice(0, 10);
}
